/**
 * Kokoro TTS — Neural text-to-speech via kokoro-js (ONNX WASM).
 *
 * Provides high-quality neural speech synthesis for English text.
 * ~92 MB model download, cached in browser Cache API by Transformers.js.
 *
 * Limitations (as of March 2026):
 * - English only — JS phonemizer for other languages not yet implemented
 * - WASM device only — WebGPU has audio distortion bugs
 * - iOS Safari unreliable — falls back to Web Speech API automatically
 *
 * Architecture:
 *   English text → Kokoro (neural, 8/10 quality)
 *   Non-English  → Web Speech API (handled by tts.ts, not this module)
 */

// ---------------------------------------------------------------------------
// Types & state
// ---------------------------------------------------------------------------

export type KokoroStatus = 'unloaded' | 'loading' | 'ready' | 'error' | 'unsupported';

let _status: KokoroStatus = 'unloaded';
let _error: string | null = null;
let _ttsInstance: any = null; // KokoroTTS instance

// Default voice — warm female American English
const DEFAULT_VOICE = 'af_heart';
const SAMPLE_RATE = 24_000;

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

export function getKokoroStatus(): KokoroStatus {
  return _status;
}

export function getKokoroError(): string | null {
  return _error;
}

export function isKokoroReady(): boolean {
  return _status === 'ready';
}

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

/**
 * Detect if the current platform can reliably run Kokoro.
 * iOS Safari has documented issues with large WASM models.
 */
function canRunKokoro(): boolean {
  if (typeof navigator === 'undefined') return false;

  // iOS Safari: unreliable WASM memory allocation for large models
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    console.log('[KokoroTTS] iOS detected — skipping Kokoro (unreliable WASM)');
    return false;
  }

  // Check for WASM support
  if (typeof WebAssembly === 'undefined') {
    console.log('[KokoroTTS] No WebAssembly support');
    return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize the Kokoro TTS engine.
 *
 * Lazy-loads the kokoro-js module and downloads the ONNX model (~92 MB).
 * Model is cached in browser Cache API by Transformers.js on first load.
 *
 * @param onProgress Optional callback for download progress
 */
export async function initKokoroTTS(
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  if (_status === 'ready' || _status === 'loading') return;

  if (!canRunKokoro()) {
    _status = 'unsupported';
    return;
  }

  _status = 'loading';
  _error = null;

  try {
    // Dynamic import to keep bundle small when Kokoro isn't used
    const { KokoroTTS } = await import('kokoro-js');

    _ttsInstance = await KokoroTTS.from_pretrained(
      'onnx-community/Kokoro-82M-v1.0-ONNX',
      {
        dtype: 'q8',     // 92 MB, near-identical quality to fp32
        device: 'wasm',  // WebGPU has audio distortion bugs
        progress_callback: onProgress
          ? (progress: any) => {
              if (progress.status === 'progress' && progress.total) {
                onProgress(progress.loaded || 0, progress.total);
              }
            }
          : undefined,
      },
    );

    _status = 'ready';
    console.log('[KokoroTTS] Model loaded successfully');
  } catch (err) {
    _status = 'error';
    _error = err instanceof Error ? err.message : String(err);
    console.warn('[KokoroTTS] Init failed (will use Web Speech API):', err);
  }
}

// ---------------------------------------------------------------------------
// Speech generation
// ---------------------------------------------------------------------------

/** Audio context singleton — reused across calls */
let _audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
  }
  return _audioCtx;
}

/** Currently playing source node — for stop() */
let _currentSource: AudioBufferSourceNode | null = null;
let _currentResolve: (() => void) | null = null;

/**
 * Generate and play speech using Kokoro neural TTS.
 *
 * @param text The text to speak (English only)
 * @param options.voice Kokoro voice ID (default: af_heart)
 * @param options.speed Speech rate multiplier (default: 1.0)
 * @returns Promise that resolves when speech playback completes
 */
export async function kokoroSpeak(
  text: string,
  options?: { voice?: string; speed?: number },
): Promise<void> {
  if (!_ttsInstance || _status !== 'ready') {
    throw new Error('Kokoro TTS not ready');
  }

  const voice = options?.voice ?? DEFAULT_VOICE;
  const speed = options?.speed ?? 1.0;

  // Generate audio
  const rawAudio = await _ttsInstance.generate(text, { voice, speed });

  // Convert Float32Array to AudioBuffer and play
  const audioCtx = getAudioContext();

  // Resume context if suspended (required after page becomes inactive)
  if (audioCtx.state === 'suspended') {
    await audioCtx.resume();
  }

  const float32Data = rawAudio.audio;
  const sampleRate = rawAudio.sampling_rate || SAMPLE_RATE;
  const audioBuffer = audioCtx.createBuffer(1, float32Data.length, sampleRate);
  audioBuffer.copyToChannel(new Float32Array(float32Data), 0);

  return new Promise<void>((resolve) => {
    const source = audioCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtx.destination);

    _currentSource = source;
    _currentResolve = resolve;

    source.onended = () => {
      _currentSource = null;
      _currentResolve = null;
      resolve();
    };

    source.start();
  });
}

/**
 * Stop any currently playing Kokoro speech.
 */
export function kokoroStop(): void {
  if (_currentSource) {
    try {
      _currentSource.stop();
    } catch {
      /* already stopped */
    }
    _currentSource = null;
  }
  if (_currentResolve) {
    _currentResolve();
    _currentResolve = null;
  }
}

/**
 * Check if Kokoro is currently playing audio.
 */
export function isKokoroSpeaking(): boolean {
  return _currentSource !== null;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/**
 * Dispose Kokoro TTS resources.
 */
export async function disposeKokoroTTS(): Promise<void> {
  kokoroStop();

  if (_audioCtx && _audioCtx.state !== 'closed') {
    try {
      await _audioCtx.close();
    } catch {
      /* ignore */
    }
    _audioCtx = null;
  }

  _ttsInstance = null;
  _status = 'unloaded';
  _error = null;
}
